import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";

import type { Schema } from "../../model";
import { zSchema } from "../../model";

import type { OrderEntity } from "~/entities/order";
import type { ServiceEntity } from "~/entities/service";
import type { useTranslation2 } from "~/shared/i18n";
import { groupBy } from "~/shared/record";
import { DEFAULT_ZOD_PARSE_PARAMS, errorMapForForms2 } from "~/shared/zod";

function useCalculatorForm(
  orderedServices: Parameters<typeof getDefaultValues>[0],
  services: ServiceEntity.Schema[],
  t: ReturnType<typeof useTranslation2>["translation"]["t"],
): [Partial<Record<string, ServiceEntity.Schema[]>>, UseFormReturn<Schema, unknown, Schema>] {
  const groupedServices = useMemo(
    () => groupBy(services, ({ category: { id } }) => id),
    [services],
  );

  return [
    groupedServices,
    useForm({
      resolver: zodResolver(zSchema, {
        ...DEFAULT_ZOD_PARSE_PARAMS,
        errorMap: errorMapForForms2(t),
      }),
      defaultValues: getDefaultValues(orderedServices, groupedServices),
      mode: "onChange",
    }),
  ];
}

function getDefaultValues(
  orderedServices: OrderEntity.Schema["services"],
  groupedServices: Partial<Record<string, ServiceEntity.Schema[]>>,
) {
  return Object.fromEntries(
    Object.entries(groupedServices)
      .filter((value): value is [string, ServiceEntity.Schema[]] => !!value[1])
      .map(([key, value]) => [
        key,
        value.map(({ id }) => ({
          id,
          count: orderedServices.find((service) => service.id === id)?.quantity ?? 0,
        })),
      ]),
  );
}

export { getDefaultValues, useCalculatorForm };
